import { PartyType } from "Api/Api";
import { useGetMerchantQuery } from "Api/Queries/Merchants/useGetMerchantQuery";
import { usePartyQuery } from "Api/Queries/Party/usePartyQuery";
import { MerchantSkeleton } from "Components/Hunter/Merchants/Detail/Tabs/Merchant/MerchantSkeleton";
import { RelatedPartiesSection } from "Components/Users/UserDetailPageContent/Components/RelatedPartyForm/RelatedPartiesSection";
import { ErrorText } from "Components/Shared/Errors/ErrorText";
import { ContactPersons } from "Components/Shared/Merchant/ContactPersons/ContactPersons";
import { Facilities } from "Components/Shared/Merchant/Facilities/Facilities";
import { MerchantStatus } from "Infrastructure/Api/Api";
import { useIsCurrentUserAdmin } from "Hooks/useIsCurrentUserAdmin";
import { useIsCurrentUserCustomerOnboarding } from "Hooks/useIsCurrentUserCustomerOnboarding";
import { useIsCurrentUserHunter } from "Hooks/useIsCurrentUserHunter";
import { getRegionCurrency } from "Utils/CurrencyUtils";
import { Resources, useResource } from "Translations/Resources";
import { Box, Divider, Stack } from "@mui/material";
import { EditableNotes } from "Components/Hunter/Merchants/Detail/Tabs/Merchant/EditableNotes";
import { LicenseShares } from "Components/Hunter/Merchants/Detail/Tabs/Merchant/LicenseShares";
import { MerchantBasicInfoSection } from "Components/Hunter/Merchants/Detail/MerchantBasicInfoSection";
import {
  type MerchantAvatarAccess,
  MerchantAvatarUpload,
} from "Components/Hunter/Merchants/Detail/MerchantAvatarUpload";

const IN_ONBOARDING_STATUSES: ReadonlyArray<MerchantStatus> = [
  MerchantStatus.InDraft,
  MerchantStatus.InvitedWaitingForConfirmation,
  MerchantStatus.Onboarding,
];

type Props = {
  publicPartyID: string;
  merchantPublicID: string;
  isReadOnly: boolean;
  isNoteHidden?: boolean;
};

export const Merchant = ({
  publicPartyID,
  merchantPublicID,
  isReadOnly,
  isNoteHidden,
}: Props) => {
  const { t } = useResource();
  const isAdmin = useIsCurrentUserAdmin();
  const isCustomerOnboarding = useIsCurrentUserCustomerOnboarding();
  const isHunter = useIsCurrentUserHunter();
  const { data: partyData, isLoading: isPartyLoading } =
    usePartyQuery(publicPartyID);
  const { data: merchantData, isLoading: isMerchantLoading } =
    useGetMerchantQuery(merchantPublicID);

  if (isPartyLoading || isMerchantLoading) {
    return <MerchantSkeleton />;
  }

  if (!partyData || !merchantData) {
    return (
      <ErrorText>{t(Resources.Components.Merchant.LoadingError)}</ErrorText>
    );
  }

  const {
    merchantUserLogin,
    merchantIdentityUserPublicID,
    facilities,
    note,
    status,
    licenseShares,
  } = merchantData;

  const canResendAccess =
    status === MerchantStatus.InvitedWaitingForConfirmation &&
    merchantUserLogin;

  // Self-registered (add-role) merchants stay Onboarding until an admin approves them — nothing
  // flips them automatically (legacy merchants activate via the owner's KYC approval instead).
  const canActivate =
    status === MerchantStatus.Onboarding && (isAdmin || isCustomerOnboarding);

  const isLegalEntity = partyData.type === PartyType.LegalEntity;
  const hasSharedCommission = licenseShares.length > 1;
  const isFacilitiesReadOnly = !(isAdmin || isCustomerOnboarding);
  // Mirrors the backend rule (MerchantLogoAuthorization): only hunters are
  // restricted, and only to the onboarding phase. Everyone else who reaches
  // this screen may edit the merchant's avatar.
  const canEditAvatar = !isHunter || IN_ONBOARDING_STATUSES.includes(status);
  const avatarAccess: MerchantAvatarAccess = isReadOnly
    ? "readOnly"
    : canEditAvatar
      ? "editable"
      : "lockedAfterOnboarding";
  // The merchant's image is the founder Merchant persona's avatar. A hunter-created company
  // has no Merchant persona until access is sent, so until then there is nothing to upload
  // to and the section stays hidden (ZS-1607).
  const hasMerchantPersona = !!merchantIdentityUserPublicID;
  const isRelatedPartiesReadOnly = !isAdmin;

  return (
    <Stack gap={8}>
      <MerchantBasicInfoSection
        partyData={partyData}
        merchantUserLogin={merchantUserLogin || "-"}
        merchantPublicID={merchantPublicID}
        canResendAccess={!!canResendAccess && !isReadOnly}
        canActivate={canActivate && !isReadOnly}
      />
      {hasMerchantPersona && (
        <MerchantAvatarUpload
          merchantPublicID={merchantPublicID}
          access={avatarAccess}
        />
      )}
      <Box>
        <ContactPersons isReadOnly publicPartyID={publicPartyID} />
        <Facilities
          isReadOnly={isFacilitiesReadOnly}
          merchantPublicID={merchantPublicID}
          facilities={facilities}
          marketCurrencyCode={
            partyData.region ? getRegionCurrency(partyData.region) : undefined
          }
        />
        {isLegalEntity && (
          <RelatedPartiesSection
            relatedParties={partyData.relatedParties}
            isReadOnly={isRelatedPartiesReadOnly}
            owningPartyID={publicPartyID}
          />
        )}
        {!isNoteHidden && (
          <>
            <Divider sx={{ mt: [4, 8], mb: 4 }} />
            <EditableNotes
              merchantPublicID={merchantPublicID}
              notes={note}
              isReadOnly={isReadOnly}
            />
          </>
        )}
        {hasSharedCommission && (
          <>
            <Divider sx={{ mt: [4, 8], mb: 4 }} />
            <LicenseShares licenseShares={licenseShares} />
          </>
        )}
      </Box>
    </Stack>
  );
};
